/**
 * `@bl/shared` — cross-process constants.
 *
 * Everything here is shared by `@bl/api`, `@bl/indexer` and `@bl/web`, which is why it
 * has to be a *pure* module: no `node:*` imports, no environment reads, no I/O. `apps/web`
 * resolves with `Bundler` resolution and the two Node apps with `NodeNext`, so the only
 * relative specifiers used below carry an explicit `.js` extension and point at modules
 * that are themselves pure.
 *
 * ── Chunk sizing: which Telegram rule set actually binds us ─────────────────────────
 *
 * Every audio byte in this project comes through teleproto's public `iterDownload()`,
 * and `iterDownload` invokes `upload.getFile` with **`precise: true`**
 * (`api-groundtruth.md` §2, §2.1; `spec.md` §10.2). That single flag decides the rules,
 * so the live caller obligations are exactly three:
 *
 *   1. `requestSize % 4096 === 0`  — the library throws
 *      `Error("requestSize must be divisible by 4096")` locally, so this one fails fast;
 *   2. `requestSize <= 1 MB`       — a server cap; the library's own default is 512 KB;
 *   3. the **offset** must be aligned, and the library does **not** validate it. With
 *      `precise: true` the protocol only demands 1 KB, but we align down to the chunk
 *      size instead: it satisfies the 1 KB rule trivially, makes every part boundary
 *      reproducible across separate HTTP range requests (which is what lets the disk
 *      cache and the client prefetch coalesce instead of fetching overlapping ranges),
 *      and reduces the head trim to a subtraction.
 *
 * A misaligned offset is the expensive one: it travels to Telegram and comes back as an
 * opaque `OFFSET_INVALID` that names no rule. Hence {@link assertAligned}.
 *
 * There is deliberately **no closed set of legal chunk sizes**, no divisor-of-one-megabyte
 * rule and no one-megabyte-window containment check in this module. Those are the
 * **non-`precise`** rules: they govern `MediaScheduler.getFile()` (which sends
 * `precise: false`) and any hand-rolled `Api.upload.GetFile` that leaves `precise` unset —
 * neither of which this project calls. They are absent rather than merely relaxed because
 * a dead invariant left lying around reads as a live one: it would reject a perfectly
 * legal `12288`-byte chunk while somebody hunts a bug that is not there.
 */
export { INK_PAIRS } from './plate.js';
export { PEAKS_RESOLUTION } from './codec/peaks.js';
/* ───────────────────────────── chunk sizing (§10.2) ────────────────────────────────── */
/** Divisor every `requestSize` handed to `upload.getFile` must satisfy. */
export const CHUNK_ALIGN = 4096;
/** Server-side ceiling on a single `upload.getFile` part, in bytes (1 MiB). */
export const MAX_REQUEST_SIZE = 1048576;
/** Our default part size: 512 KiB — `% CHUNK_ALIGN` and comfortably under the cap. */
export const DEFAULT_CHUNK_BYTES = 524288;
/**
 * Is `n` a chunk size `upload.getFile` will accept on the `precise: true` path?
 *
 * Positive, integral, `% 4096` and `<= 1 MiB`. Nothing else — `12288` is legal, and that
 * is the point: it is a multiple of 4096 under the cap, and only the non-`precise`
 * divisor rule this project does not use ever rejected it.
 */
export function isLegalRequestSize(n) {
    return Number.isInteger(n) && n > 0 && n % CHUNK_ALIGN === 0 && n <= MAX_REQUEST_SIZE;
}
/**
 * Round `offset` **down** to the nearest multiple of `chunk`.
 *
 * `offset` is a byte position in a file, so it is non-negative by construction; the
 * remainder therefore needs no sign correction. The result is `<= offset`, and
 * `offset - alignDown(offset, chunk) < chunk` — that difference is the head trim to drop
 * from the first part yielded by `iterDownload`.
 */
export function alignDown(offset, chunk) {
    return offset - (offset % chunk);
}
/**
 * Dev-only assertion covering the three live rules of `spec.md` §10.2. Two of the three
 * failures surface only as an opaque error from Telegram, so asserting locally turns
 * hours of guessing into an immediate stack trace. Every message names the offending
 * value and which rule it broke.
 */
export function assertAligned(offset, requestSize, chunk = DEFAULT_CHUNK_BYTES) {
    if (offset % chunk !== 0) {
        throw new Error(`offset ${offset} not ${chunk}-aligned`);
    }
    if (requestSize % CHUNK_ALIGN !== 0) {
        throw new Error(`requestSize ${requestSize} not % ${CHUNK_ALIGN}`);
    }
    if (requestSize > MAX_REQUEST_SIZE) {
        throw new Error(`requestSize ${requestSize} over the 1MB cap (${MAX_REQUEST_SIZE})`);
    }
}
/* ────────────────────────────── media types (§6.11) ────────────────────────────────── */
/**
 * IANA-registered audio media types we are willing to put in a `Content-Type`
 * (`spec.md` §6.11). Anything else falls back by file extension, then to `audio/mpeg`.
 */
export const AUDIO_MIME_ALLOWLIST = Object.freeze([
    'audio/mpeg',
    'audio/mp4',
    'audio/aac',
    'audio/ogg',
    'audio/flac',
    'audio/wav',
]);
/**
 * Lowercase, dot-less file extension → media type.
 *
 * **`.opus` maps to `audio/ogg`, never `audio/opus`.** RFC 7587 registers `audio/opus`
 * as a raw RTP payload format, not a file container; Opus-in-Ogg — which is what Telegram
 * actually ships — is `audio/ogg`. Sending `audio/opus` makes Safari refuse the resource.
 */
export const EXT_TO_MIME = Object.freeze({
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    mp4: 'audio/mp4',
    aac: 'audio/aac',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    opus: 'audio/ogg',
    flac: 'audio/flac',
    wav: 'audio/wav',
});
/* ─────────────────────────────── other shared limits ───────────────────────────────── */
/**
 * Bot API `getFile` hard ceiling — exactly 20 MiB (`MAX_DOWNLOAD_FILE_SIZE = 20 << 20`
 * in Telegram's own Bot API server). Rows above this are marked bot-undownloadable so
 * the inline-audio fallback never attempts them (`spec.md` §10.1).
 */
export const BOT_GETFILE_MAX_BYTES = 20 * 1024 * 1024;
/** `playback_state.repeat_mode` domain, in the DDL's `CHECK` order (`spec.md` §5.2). */
export const REPEAT_MODES = Object.freeze(['off', 'all', 'one']);
/**
 * Gap between adjacent `queue_items.position` values on a full queue write
 * (`position = i * QUEUE_POSITION_STEP`, `spec.md` §6.7). The gap is what lets a
 * single-item reorder be one `UPDATE` to a midpoint instead of a renumbering pass.
 */
export const QUEUE_POSITION_STEP = 1024;
/** Tolerance for comparing fractional positions/volumes that survived a JSON round-trip. */
export const POSITION_EPSILON = 1e-6;
//# sourceMappingURL=constants.js.map